import { z } from 'zod';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  Signal,
} from '@angular/core';
import {
  TournamentInfoItemConfig,
  TournamentInfoItemVariant,
  TournamentInfoItemComponent,
} from '../tournament-info-item/tournament-info-item.component';
import { TournamentDataModel } from 'src/app/core/services/tournament/models/tournament-data-model';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { TournamentStatusData } from 'src/app/core/services/tournament/models/tournament-data-model';
import { TranslocoService } from '@jsverse/transloco';
import { ThemePipe } from '../../../../core/pipes/theme.pipe';
import { ButtonComponent } from '../../../../ui/buttons/button/button.component';
import { ModePillComponent } from 'src/app/ui/pills/mode-pill/mode-pill.component';
import { IconComponent } from '../../../../ui/icon/icon/icon.component';
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { CurrencyIconComponent } from '../../../../ui/currency/currency-icon/currency-icon.component';
import { SplicedBackgroundComponent } from '../../../../ui/surface/spliced-background/spliced-background.component';
import { TournamentPaymentEventData } from 'src/app/core/services/tiers/models/payment-events';
import { TournamentStatus } from 'src/app/core/api/player/schemas/tournaments';
import { marblesScoreToTimeOrDnf } from 'src/app/shared/utils/marbles-time.utils';

export const TournamentInfoVariantSchema = z.enum(['full', 'data-and-info']);
export type TournamentInfoVariant = z.infer<typeof TournamentInfoVariantSchema>;

@Component({
  selector: 'app-tournament-info-card',
  templateUrl: './tournament-info-card.component.html',
  styleUrls: ['./tournament-info-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    NgStyle,
    NgTemplateOutlet,
    IconComponent,
    CurrencyIconComponent,
    ModePillComponent,
    TournamentInfoItemComponent,
    ButtonComponent,
    ThemePipe,
    SplicedBackgroundComponent,
  ],
})
export class TournamentInfoCardComponent {
  currentTournamentData = input.required<TournamentDataModel>();
  currentTime = input.required<Date>();
  variant = input<TournamentInfoVariant>('full');
  readonly isMarbles = input.required<boolean>();
  // Play goes inert while any payment intent is in flight — the service latch would drop the
  // second intent silently, and this is the visible half of that guard. Must match the wager
  // cards' isDisabled treatment. Bound down by the container that holds PaymentService.
  readonly paymentInFlight = input.required<boolean>();
  clickEvent = output<TournamentPaymentEventData>();
  // The question mark and Results buttons ask the parent to open the tutorial and the leaderboard
  // drawer, so the card itself opens nothing.
  readonly tutorialRequestedEvent = output<TournamentDataModel>();
  readonly leaderboardRequestedEvent = output<TournamentDataModel>();

  private translocoService = inject(TranslocoService);
  private themeService = inject(ThemeService);

  protected readonly splicedBg = this.themeService.splicedBackgroundPlacement('tournament-card');

  readonly tournamentStatusData: Signal<TournamentStatusData> = computed(() => {
    return this.currentTournamentData().refreshTimeStatus(this.currentTime());
  });

  readonly status: Signal<TournamentStatus> = computed(() => {
    return this.tournamentStatusData().status;
  });

  readonly cardStyle: Signal<[string, string][]> = computed(() => {
    const statusData = this.tournamentStatusData();
    const state = statusData.live ? 'active' : 'inactive';
    const key = 'card-tournament';

    if (this.variant() !== 'full') {
      return [];
    }

    return [
      ['background', key],
      ['border-radius', key],
      ['border', `card-tournament-${state}`],
    ];
  });

  // The prize pool block is shared by both layouts. The compact card has its own type and label
  // colour (card.tournament.compact.prize-*), so the leaderboard drawer's card, which shares the
  // prize-pool tokens, stays as it is.
  private prizeKey(part: 'label' | 'value'): string {
    return this.isCompact()
      ? `card-tournament-compact-prize-${part}`
      : `card-tournament-prize-pool-${part}`;
  }

  readonly prizePoolLabelStyles = computed((): [string, string][] => [
    ['type', this.prizeKey('label')],
    ['text-color', this.prizeKey('label')],
  ]);

  readonly prizePoolShadowStyles = computed((): [string, string][] => [
    ['type', this.prizeKey('value')],
    ['text-shadow', 'card-tournament-prize-pool'],
  ]);

  readonly prizePoolValueStyles = computed((): [string, string][] => [
    ['type', this.prizeKey('value')],
    ['text-outer-border', 'card-tournament-prize-pool'],
    ['text-color', 'card-tournament-prize-pool-value'],
  ]);

  readonly hasPrizeShadow = computed(() => this.themeService.hasWagerCardPrizeShadow());

  readonly pillLabel: Signal<string> = computed(() => {
    const statusData = this.tournamentStatusData();
    return statusData.pillState;
  });

  readonly activeState: Signal<'active' | 'inactive'> = computed(() => {
    const statusData = this.tournamentStatusData();
    return statusData.live ? 'active' : 'inactive';
  });

  readonly isDisabled: Signal<boolean> = computed(() => {
    const statusData = this.tournamentStatusData();
    return statusData.disabled;
  });

  readonly showButtons: Signal<boolean> = computed(() => {
    return this.variant() === 'full';
  });

  // The lobby card's compact layout (tournamentCard.layout): two columns with text buttons. The
  // leaderboard drawer's data-and-info card never changes.
  readonly isCompact: Signal<boolean> = computed(
    () =>
      this.variant() === 'full' && this.themeService.tournamentCardConfig().layout === 'compact',
  );

  // Both layouts' Results and Play buttons: the compact card's are small, at the wager cards'
  // button height (see .compact-buttons).
  readonly buttonSize: Signal<'small' | 'medium'> = computed(() =>
    this.isCompact() ? 'small' : 'medium',
  );

  // Compact layout: one status line in place of the stacked card's pill and info items. The lobby
  // never shows a FINISHED tournament, so that case is only here to keep the switch exhaustive.
  readonly compactStatus: Signal<string> = computed(() => {
    const tournamentData = this.currentTournamentData();
    const statusData = this.tournamentStatusData();
    switch (statusData.status) {
      case 'ACCEPTING_SCORES':
        return this.translocoService.translate('components.tournament.time-left-inline', {
          time: statusData.timeRemaining,
        });
      case 'CREATED':
        return `${this.translocoService.translate('components.tournament.starts')} ${tournamentData.startDate}`;
      case 'COLLECTING_SCORES':
        return this.translocoService.translate('components.tournament.ending');
      case 'FINISHED':
        return `${this.translocoService.translate('components.tournament.finished')} ${tournamentData.finishDate}`;
    }
  });

  readonly compactTitle: Signal<string> = computed(() =>
    this.translocoService.translate(
      this.status() === 'ACCEPTING_SCORES'
        ? 'components.tournament.live-tournament'
        : 'components.tournament.tournament',
    ),
  );

  readonly compactStatusStyles: [string, string][] = [
    ['type', 'card-tournament-compact-status'],
    ['text-color', 'card-tournament-compact-status'],
  ];

  readonly compactTitleStyles: [string, string][] = [
    ['type', 'card-tournament-compact-title'],
    ['text-color', 'card-tournament-compact-title'],
  ];

  readonly compactSubtitleStyles: [string, string][] = [
    ['type', 'card-tournament-compact-subtitle'],
    ['text-color', 'card-tournament-compact-subtitle'],
  ];

  readonly hasSecondaryCurrency = computed(() => this.themeService.hasSecondaryCurrency);
  readonly resultsButtonLabel = this.translocoService.translate('components.tournament.results');

  readonly playButtonLabel = computed(() =>
    this.translocoService.translate('components.tournament.play'),
  );

  readonly playButtonAmount = computed(() => this.currentTournamentData().formattedEntryFee);

  // The player's own score (time, in Marbles) once they have entered. The stacked card shows it as
  // an info item; the compact card as a line under the subtitle.
  readonly userScoreItem: Signal<TournamentInfoItemConfig | undefined> = computed(() => {
    const status = this.status();
    const tournamentData = this.currentTournamentData();
    if (
      (status === 'ACCEPTING_SCORES' || status === 'COLLECTING_SCORES' || status === 'FINISHED') &&
      tournamentData.userScore !== null
    ) {
      return {
        variant: TournamentInfoItemVariant.Count,
        label: this.translocoService.translate(
          this.isMarbles() ? 'components.tournament.your-time' : 'components.tournament.your-score',
        ),
        value: this.isMarbles()
          ? marblesScoreToTimeOrDnf(tournamentData.userScore)
          : tournamentData.userScore.toLocaleString(),
        showPulse: false,
      };
    }
    return undefined;
  });

  readonly infoItems: Signal<TournamentInfoItemConfig[]> = computed(() => {
    const status = this.status();
    const tournamentData = this.currentTournamentData();
    const statusData = this.tournamentStatusData();

    const items: TournamentInfoItemConfig[] = [];

    if (status === 'CREATED') {
      items.push({
        variant: TournamentInfoItemVariant.Default,
        label: this.translocoService.translate('components.tournament.start-date'),
        value: tournamentData.startDate,
        showPulse: false,
      });
    } else if (status === 'ACCEPTING_SCORES') {
      items.push({
        variant: TournamentInfoItemVariant.Time,
        label: this.translocoService.translate('components.tournament.time-left'),
        value: statusData.timeRemaining,
        showPulse: true,
      });
    } else if (status === 'FINISHED') {
      items.push({
        variant: TournamentInfoItemVariant.Time,
        label: this.translocoService.translate('components.tournament.finished'),
        value: tournamentData.finishDate,
        showPulse: false,
      });
    }

    const userScoreItem = this.userScoreItem();
    if (userScoreItem) {
      items.push(userScoreItem);
    }

    return items;
  });

  readonly prizePoolLabel = this.translocoService.translate('components.tournament.prize-pool');

  readonly totalPrizePool = computed(() => this.currentTournamentData().prizePool);

  readonly ctaResult = computed(() => {
    const data = this.currentTournamentData();
    if (data.status !== 'FINISHED') {
      return undefined;
    }
    const prizeInfo = data.currentPrizeInfo();
    if (prizeInfo.prizeAmount && prizeInfo.position) {
      return this.translocoService.translate('components.tournament.result-win', {
        position: prizeInfo.position,
        amount: prizeInfo.prizeAmount,
      });
    }
    return undefined;
  });

  get cta(): string {
    return this.translocoService.translate(
      this.isMarbles()
        ? 'components.tournament.get-top-time'
        : 'components.tournament.get-top-score',
    );
  }
  readonly collectingScoresCta = this.translocoService.translate(
    'components.tournament.awaiting-final-results',
  );

  // The line under the prize pool (stacked) or the title (compact): the player's result once the
  // tournament is finished, if they won anything; otherwise the call to action.
  readonly subtitle: Signal<string | undefined> = computed(() => {
    switch (this.status()) {
      case 'FINISHED':
        return this.ctaResult();
      case 'COLLECTING_SCORES':
        return this.collectingScoresCta;
      default:
        return this.cta;
    }
  });

  readonly subtitleStyles: Signal<[string, string][]> = computed(() => {
    if (this.isCompact()) {
      return this.compactSubtitleStyles;
    }
    return this.status() === 'FINISHED' ? this.ctaResultStyles : this.ctaStyles;
  });

  get iconInfoColor(): string {
    return `--card-tournament-icon-info-fill`;
  }

  readonly ctaStyles: [string, string][] = [
    ['type', 'card-tournament-status-value'],
    ['text-color', 'card-tournament-status-value'],
  ];

  readonly ctaResultStyles: [string, string][] = [
    ['type', 'card-tournament-status-value'],
    ['text-color', 'card-tournament-status'],
  ];

  public onTutorialClick(): void {
    this.tutorialRequestedEvent.emit(this.currentTournamentData());
  }

  public onViewClick(): void {
    if (this.isDisabled()) {
      return;
    }
    this.leaderboardRequestedEvent.emit(this.currentTournamentData());
  }

  public async onPlayClick(): Promise<void> {
    if (this.paymentInFlight()) {
      return;
    }
    this.clickEvent.emit({
      type: 'tournament',
      tournamentData: this.currentTournamentData(),
    });
  }
}
